import { useEffect, useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { getReviewQueue, updateReviewItem } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { FileSearch, Loader2, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

export default function ReviewQueuePage() {
  const { t, language } = useLanguage();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('pending');
  const [filterType, setFilterType] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [resolutionNotes, setResolutionNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const fetchItems = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterStatus !== 'all') params.status = filterStatus;
      if (filterType !== 'all') params.entity_type = filterType;
      
      const response = await getReviewQueue(params);
      setItems(response.data);
    } catch (error) {
      console.error('Failed to fetch review queue:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
  }, [filterStatus, filterType]);

  const handleResolve = (item, action) => {
    setSelectedItem({ ...item, action });
    setResolutionNotes('');
    setDialogOpen(true);
  };

  const handleSubmitResolution = async () => {
    if (!selectedItem) return;
    
    setSaving(true);
    try {
      await updateReviewItem(selectedItem.id, {
        status: selectedItem.action,
        resolution_notes: resolutionNotes
      });
      toast.success(t('success.updated'));
      setDialogOpen(false);
      fetchItems();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error.general'));
    } finally {
      setSaving(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800',
      resolved: 'bg-blue-100 text-blue-800',
    };
    return (
      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${styles[status] || styles.pending}`}>
        {t(status)}
      </span>
    );
  };

  const getIssueTypeLabel = (type) => {
    const labels = {
      unknown_region: 'منطقة غير معروفة',
      unknown_city: 'مدينة غير معروفة',
      unknown_district: 'حي غير معروف',
      missing_category: 'تصنيف مفقود',
      missing_subcategory: 'تصنيف فرعي مفقود',
      category_mismatch: 'عدم تطابق التصنيف',
      duplicate_row: 'صف مكرر',
      invalid_coordinates: 'إحداثيات غير صحيحة',
      blank_required: 'حقول مطلوبة فارغة',
    };
    return labels[type] || type;
  };

  return (
    <div className="space-y-6" data-testid="review-queue-page">
      <Card className="bg-white border border-gray-200">
        <CardHeader>
          <div className="flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center">
            <CardTitle className="flex items-center gap-2">
              <FileSearch className="w-5 h-5" />
              {t('review_queue')}
            </CardTitle>
            <div className="flex gap-3">
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-32" data-testid="review-filter-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('all')}</SelectItem>
                  <SelectItem value="pending">{t('pending')}</SelectItem>
                  <SelectItem value="approved">{t('approved')}</SelectItem>
                  <SelectItem value="rejected">{t('rejected')}</SelectItem>
                </SelectContent>
              </Select>
              <Select value={filterType} onValueChange={setFilterType}>
                <SelectTrigger className="w-32" data-testid="review-filter-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('all')}</SelectItem>
                  <SelectItem value="project">{t('project')}</SelectItem>
                  <SelectItem value="outlet">{t('outlet')}</SelectItem>
                  <SelectItem value="region">{t('region')}</SelectItem>
                  <SelectItem value="city">{t('city')}</SelectItem>
                  <SelectItem value="district">{t('district')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-gray-50">
                <TableHead className="font-medium">{t('entity_type')}</TableHead>
                <TableHead className="font-medium">{t('issue_type')}</TableHead>
                <TableHead className="font-medium">{t('original_value')}</TableHead>
                <TableHead className="font-medium">{t('suggested_value')}</TableHead>
                <TableHead className="font-medium">{t('status')}</TableHead>
                <TableHead className="font-medium">{t('created_at')}</TableHead>
                <TableHead className="font-medium text-center">{t('actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin mx-auto text-[#0A4C3A]" />
                  </TableCell>
                </TableRow>
              ) : items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                    {t('no_data')}
                  </TableCell>
                </TableRow>
              ) : (
                items.map((item) => (
                  <TableRow key={item.id} data-testid={`review-row-${item.id}`}>
                    <TableCell className="font-medium">{t(item.entity_type)}</TableCell>
                    <TableCell>{getIssueTypeLabel(item.issue_type)}</TableCell>
                    <TableCell className="max-w-xs truncate">{item.original_value || '-'}</TableCell>
                    <TableCell className="max-w-xs truncate">{item.suggested_value || '-'}</TableCell>
                    <TableCell>{getStatusBadge(item.status)}</TableCell>
                    <TableCell className="text-sm text-gray-500">
                      {item.created_at ? new Date(item.created_at).toLocaleDateString(language === 'ar' ? 'ar-SA' : 'en-US') : '-'}
                    </TableCell>
                    <TableCell>
                      {item.status === 'pending' && (
                        <div className="flex justify-center gap-2">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleResolve(item, 'approved')}
                            className="text-green-600 hover:text-green-700 hover:bg-green-50"
                            data-testid={`approve-${item.id}`}
                          >
                            <CheckCircle className="w-4 h-4 me-1" />
                            {t('approve')}
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleResolve(item, 'rejected')}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            data-testid={`reject-${item.id}`}
                          >
                            <XCircle className="w-4 h-4 me-1" />
                            {t('reject')}
                          </Button>
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Resolution Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {selectedItem?.action === 'approved' ? t('approve') : t('reject')}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600 mb-1">{t('original_value')}:</p>
              <p className="font-medium">{selectedItem?.original_value || '-'}</p>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">{t('resolution_notes')}</label>
              <Textarea
                value={resolutionNotes}
                onChange={(e) => setResolutionNotes(e.target.value)}
                rows={3}
                placeholder={t('notes')}
                data-testid="resolution-notes"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              {t('cancel')}
            </Button>
            <Button 
              onClick={handleSubmitResolution} 
              disabled={saving}
              className={selectedItem?.action === 'approved' 
                ? 'bg-green-600 hover:bg-green-700' 
                : 'bg-red-600 hover:bg-red-700'
              }
              data-testid="submit-resolution"
            >
              {saving && <Loader2 className="w-4 h-4 me-2 animate-spin" />}
              {selectedItem?.action === 'approved' ? t('approve') : t('reject')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
